import React from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';

import { Form, Input, LinkWithValidationContext, ValidationContext } from './inputs';
import { SapPoToAmountEntry, SapPoToAmountRequestAction } from './asyncTasks';
import { currencyFormatter } from './utils';
import { getImportTaskPoDetails } from '../apiClient';
import { stopPropagation } from './events';
import { EntitlementCurrencyConfig } from '../app/appConfig';
import BigNumber from './bigNumber';

interface PoDetailsResponse {
    availableSapPos: ManualSapPoDetailsResponse[];
}

interface Props {
    onSubmit: (sapPoToAmountEntries: Array<SapPoToAmountEntry>, action: SapPoToAmountRequestAction) => void;
    onClose: () => void;
    errorHandler: (err) => void;

    fileAmount: number;
    currency: EntitlementCurrencyConfig;
}

interface ManualSapPoDetailsResponse {
    poItem: string;
    poNumber: string;
    availableAmount: string;
    originalAmount: string;
    terminationDate: string;
}

interface UserSapPoDetails extends ManualSapPoDetailsResponse {
    requestedAmount: string;
    isEditable: boolean;
}

interface State {
    userSapPoDetails: Array<UserSapPoDetails>;
}

export default class SapPoToAmountEntryEditableDialog extends React.Component<Props, State> {
    private validationContext = new ValidationContext();
    private formatter = currencyFormatter(this.props.currency).format;

    constructor(props) {
        super(props);
        this.state = {
            userSapPoDetails: [],
        };
    }

    async componentDidMount(): Promise<void> {
        try {
            const sapPoResponse: PoDetailsResponse = await getImportTaskPoDetails();
            const sapPoDetails = sapPoResponse.availableSapPos.map(
                (poResponse: ManualSapPoDetailsResponse, index: number) => {
                    return {
                        poNumber: poResponse.poNumber,
                        poItem: poResponse.poItem,
                        availableAmount: poResponse.availableAmount,
                        originalAmount: poResponse.originalAmount,
                        terminationDate: poResponse.terminationDate,
                        requestedAmount: '0',
                        row: index,
                        isEditable: false,
                    };
                }
            );
            this.setSortedState(sapPoDetails);
        } catch (err) {
            this.props.onClose();
            this.props.errorHandler(err);
        }
    }

    renderWaitingModal() {
        const { onClose } = this.props;
        return (
            <Modal
                backdrop="static"
                centered
                dialogClassName="modal-small"
                keyboard={false}
                onHide={() => onClose()}
                show
            >
                <Modal.Header>
                    <Modal.Title>SAP PO Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <div className="mv3">
                            <div className="progress">
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div>Please wait while WINGSII data is loading...</div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="wfp-btn" onClick={() => onClose()} style={{ marginRight: 10 }} type="button">
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderReadyModal() {
        const { onClose, onSubmit, fileAmount } = this.props;
        const { userSapPoDetails } = this.state;
        const selectedSapPaymentProposals = userSapPoDetails.filter((sapPaymentRequest) =>
            new BigNumber(sapPaymentRequest.requestedAmount).isGreaterThan(0)
        );
        const isAnyCellEdited = Boolean(userSapPoDetails.find((sapPaymentRequest) => sapPaymentRequest.isEditable));
        const isAnyCellSelected = Boolean(selectedSapPaymentProposals.length > 0);

        const allocatedAmount: BigNumber = userSapPoDetails.reduce((prevValue, currentValue) => {
            const currentBigNumber = new BigNumber(currentValue.requestedAmount);
            if (currentBigNumber.isNaN()) {
                return prevValue;
            } else {
                return prevValue.plus(currentBigNumber);
            }
        }, new BigNumber(0));
        const difference: BigNumber = allocatedAmount.minus(fileAmount);
        const doesAnyDifferenceInAmountExist = !difference.isEqualTo(0);

        const sapPoToAmounts: Array<SapPoToAmountEntry> = userSapPoDetails
            .map((sapPoToAmount) => {
                return {
                    poItem: sapPoToAmount.poItem,
                    poNumber: sapPoToAmount.poNumber,
                    poAmount: sapPoToAmount.requestedAmount,
                };
            })
            .filter((sapPoToAmount) => new BigNumber(sapPoToAmount.poAmount).isGreaterThan(0));

        return (
            <Modal
                backdrop="static"
                centered
                dialogClassName="modal-large"
                keyboard={false}
                onHide={() => onClose()}
                show
            >
                <Modal.Header>
                    <Modal.Title>SAP PO Details</Modal.Title>
                </Modal.Header>
                <Form
                    className="mv1 wfp-form"
                    context={this.validationContext}
                    onSubmit={() => onSubmit(sapPoToAmounts, SapPoToAmountRequestAction.park)}
                >
                    <Modal.Body>
                        {fileAmount && !Number.isNaN(fileAmount) && (
                            <h6 className="m-3">
                                File amount:
                                <small className="text-muted">{this.formatter(fileAmount)} JOD</small>
                            </h6>
                        )}
                        {allocatedAmount && (
                            <h6 className="m-3">
                                Selected allocation:
                                <small className="text-muted">
                                    {allocatedAmount.isNaN()
                                        ? this.formatter(0)
                                        : this.formatter(allocatedAmount.toNumber())}{' '}
                                    JOD
                                </small>
                            </h6>
                        )}
                        <h6 className="m-3">
                            Difference:
                            <small className={doesAnyDifferenceInAmountExist ? 'text-danger' : 'text-success'}>
                                {this.formatter(difference.toNumber())} JOD
                            </small>
                        </h6>
                        {this.renderBody(userSapPoDetails)}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="wfp-btn" onClick={() => onClose()} style={{ marginRight: 10 }} type="button">
                            Close
                        </button>
                        <button
                            className="wfp-btn--primary"
                            disabled={doesAnyDifferenceInAmountExist || isAnyCellEdited || !isAnyCellSelected}
                            type="submit"
                        >
                            Park PO Update
                        </button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }

    render() {
        const { userSapPoDetails } = this.state;
        if (userSapPoDetails.length > 0) {
            return this.renderReadyModal();
        } else {
            return this.renderWaitingModal();
        }
    }

    private setSortedState(sapPoToAmountEntries: UserSapPoDetails[]) {
        const sortedState = this.sortPOToAmount(sapPoToAmountEntries);
        this.setState({ userSapPoDetails: sortedState });
    }

    private sortPOToAmount(userSapPaymentRequest: Array<UserSapPoDetails>) {
        const sortedAmounts = userSapPaymentRequest
            .slice(0)
            .sort((firstEl: UserSapPoDetails, secondEl: UserSapPoDetails) => {
                return (
                    moment(firstEl.terminationDate).diff(moment(secondEl.terminationDate)) ||
                    parseFloat(firstEl.poNumber) - parseFloat(secondEl.poNumber) ||
                    parseFloat(firstEl.poItem) - parseFloat(secondEl.poItem)
                );
            });
        const reversedSorted = sortedAmounts.reverse();
        return reversedSorted;
    }

    private renderBody(sapPaymentRequestDetails: UserSapPoDetails[]) {
        return (
            <div>
                <div className="form-group">
                    {this.renderTableWithSapPaymentRequestProposals(sapPaymentRequestDetails)}
                </div>
            </div>
        );
    }

    private renderTableWithSapPaymentRequestProposals(sapPaymentRequestDetails: UserSapPoDetails[]) {
        return this.renderTable(sapPaymentRequestDetails);
    }

    private renderEditableAmountFields(value: string, sapPaymentRequest: UserSapPoDetails) {
        if (sapPaymentRequest.isEditable) {
            return (
                <Input
                    className="form-control"
                    context={this.validationContext}
                    errorMessages={{
                        min: 'Amount must be positive',
                        max: 'Amount must be less or equal available amount',
                    }}
                    hideErrorBox={true}
                    isFocused={true}
                    max={parseFloat(sapPaymentRequest.availableAmount)}
                    min={0}
                    name={`requestedAmount${sapPaymentRequest.poItem}`}
                    onChange={(event) =>
                        this.handleCellEdit({
                            name: 'requestedAmount',
                            id: `${sapPaymentRequest.poNumber}-${sapPaymentRequest.poItem}`,
                            newValue: event.target.value,
                        })
                    }
                    required={true}
                    step={0.001}
                    type="number"
                    value={Number.isNaN(parseFloat(value)) ? '' : value}
                />
            );
        } else {
            return this.formatter(parseFloat(sapPaymentRequest.requestedAmount));
        }
    }

    private renderCellEditButton(sapPaymentRequest: UserSapPoDetails) {
        return (
            <LinkWithValidationContext
                context={this.validationContext}
                onClick={stopPropagation((event) => {
                    event.preventDefault();
                    this.handleCellClick({
                        id: `${sapPaymentRequest.poNumber}-${sapPaymentRequest.poItem}`,
                    });
                })}
            >
                {sapPaymentRequest.isEditable ? 'Save' : 'Edit'}
            </LinkWithValidationContext>
        );
    }

    private handleCellClick = (rowToEdit: { id: string }) => {
        const editedAmountPo = this.state.userSapPoDetails.find(
            (sapPaymentRequest) => `${sapPaymentRequest.poNumber}-${sapPaymentRequest.poItem}` === rowToEdit.id
        );
        const isCurrentlyEditable = editedAmountPo.isEditable;
        const allOther = this.state.userSapPoDetails
            .filter(
                (sapPaymentRequest) =>
                    sapPaymentRequest.poItem !== editedAmountPo.poItem ||
                    sapPaymentRequest.poNumber !== editedAmountPo.poNumber
            )
            .map((sapPaymentRequest) => {
                return { ...sapPaymentRequest, isEditable: false };
            });
        const newState = [...allOther, { ...editedAmountPo, isEditable: !isCurrentlyEditable }];
        this.setSortedState(newState);
    };

    private handleCellEdit = (rowToEdit: { name: keyof UserSapPoDetails; id: string; newValue: string }) => {
        const editedField = rowToEdit.name;
        const value = rowToEdit.newValue;
        const editedSapPaymentProposal = this.state.userSapPoDetails.find(
            (sapPaymentRequest) => `${sapPaymentRequest.poNumber}-${sapPaymentRequest.poItem}` === rowToEdit.id
        );
        const allOther = this.state.userSapPoDetails.filter(
            (sapPaymentRequest) =>
                sapPaymentRequest.poItem !== editedSapPaymentProposal.poItem ||
                sapPaymentRequest.poNumber !== editedSapPaymentProposal.poNumber
        );
        const newState = [...allOther, { ...editedSapPaymentProposal, [editedField]: value }];
        this.setSortedState(newState);
    };

    private renderTable(sapPaymentRequestDetails: UserSapPoDetails[]) {
        return (
            <div className="pre-scrollable m-3">
                <table className="wfp-table--striped mt3 table-hover">
                    {this.renderTableHeaderForSapPaymentRequestProposals()}
                    <tbody>
                        {sapPaymentRequestDetails.map((sapPaymentRequest: UserSapPoDetails) => {
                            return (
                                <tr key={`${sapPaymentRequest.poNumber}-${sapPaymentRequest.poItem}`}>
                                    <td>{sapPaymentRequest.poNumber ? sapPaymentRequest.poNumber : '-'}</td>
                                    <td>{sapPaymentRequest.poItem}</td>
                                    <td>{this.formatter(parseFloat(sapPaymentRequest.availableAmount))}</td>
                                    <td>
                                        {this.renderEditableAmountFields(
                                            sapPaymentRequest.requestedAmount,
                                            sapPaymentRequest
                                        )}
                                    </td>
                                    <td>
                                        {moment
                                            .tz(sapPaymentRequest.terminationDate, 'CET')
                                            .endOf('day')
                                            .format('YYYY-MM-DD HH:mm:ss')}{' '}
                                        CET
                                    </td>
                                    {<td>{this.renderCellEditButton(sapPaymentRequest)}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    private renderTableHeaderForSapPaymentRequestProposals() {
        return (
            <thead>
                <tr>
                    <th>PO Number</th>
                    <th>Line Item</th>
                    <th>Available Amount</th>
                    <th>Requested Amount</th>
                    <th>TDD</th>
                    <th>Action</th>
                </tr>
            </thead>
        );
    }
}
